/**
 * Sous-pages des Réglages (lot Simplicité S10) — source unique.
 *
 * Chaque section a sa propre route `/reglages/<slug>`. Le menu latéral
 * (desktop), la liste (téléphone), les gardes de route et la redirection de
 * `/reglages` dérivent tous de ce tableau.
 *
 * Deux familles :
 *   - « Moi » : pour tout le monde (profil, sécurité) ;
 *   - « Cabinet · admin » : réservé aux ADMIN — un CGP ne voit ni l'entrée
 *     ni la page (garde de route, pas seulement le menu).
 * La bibliothèque du cabinet reste lisible par les CGP (ADR 0017) : pour un
 * admin elle vit dans « Modèles de documents », pour un CGP elle a sa propre
 * entrée en lecture seule.
 *
 * Logique pure (testée), sans React.
 */

import {
  BarChart3,
  Building2,
  FileStack,
  History,
  KeyRound,
  Library,
  Palette,
  ScrollText,
  Sparkles,
  UserRound,
  Users,
  Workflow,
  type LucideIcon,
} from "lucide-react";

import type { OnboardingStatusResponse } from "@/lib/api/auth";

export type SettingsSlug =
  | "profil"
  | "securite"
  | "bibliotheque"
  | "cabinet"
  | "identite-reglementaire"
  | "equipe"
  | "modeles"
  | "apparence-documents"
  | "automatisations"
  | "modules-ia"
  | "consommation"
  | "journal";

export type SettingsGroup = "me" | "cabinet";

export interface SettingsSection {
  slug: SettingsSlug;
  label: string;
  /** Une phrase : sous-titre de la page et de la ligne de liste (téléphone). */
  description: string;
  icon: LucideIcon;
  group: SettingsGroup;
  /** `"admin"` = ADMIN seulement ; `"member"` = CGP seulement ; `"all"`. */
  audience: "all" | "admin" | "member";
}

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  {
    slug: "profil",
    label: "Mon profil",
    description: "Votre nom, votre adresse e-mail et votre rôle dans le cabinet.",
    icon: UserRound,
    group: "me",
    audience: "all",
  },
  {
    slug: "securite",
    label: "Sécurité",
    description: "Mot de passe et double authentification.",
    icon: KeyRound,
    group: "me",
    audience: "all",
  },
  {
    slug: "bibliotheque",
    label: "Bibliothèque du cabinet",
    description: "Procédures, plaquettes et supports partagés par le cabinet.",
    icon: Library,
    group: "cabinet",
    audience: "member",
  },
  {
    slug: "cabinet",
    label: "Cabinet",
    description: "Nom du cabinet et coordonnées affichées dans l'application.",
    icon: Building2,
    group: "cabinet",
    audience: "admin",
  },
  {
    slug: "identite-reglementaire",
    label: "Identité réglementaire",
    description: "Les mentions légales qui alimentent le DER et la lettre de mission.",
    icon: ScrollText,
    group: "cabinet",
    audience: "admin",
  },
  {
    slug: "equipe",
    label: "Équipe",
    description: "Les membres du cabinet, leurs rôles et leurs accès.",
    icon: Users,
    group: "cabinet",
    audience: "admin",
  },
  {
    slug: "modeles",
    label: "Modèles de documents",
    description: "Modèles PDF du cabinet et bibliothèque de ressources.",
    icon: FileStack,
    group: "cabinet",
    audience: "admin",
  },
  {
    slug: "apparence-documents",
    label: "Apparence des documents",
    description: "Logo, couleur, police et signature des PDF remis aux clients.",
    icon: Palette,
    group: "cabinet",
    audience: "admin",
  },
  {
    slug: "automatisations",
    label: "Automatisations",
    description: "Ce que l'IA fait seule après chaque note, et son bilan récent.",
    icon: Workflow,
    group: "cabinet",
    audience: "admin",
  },
  {
    slug: "modules-ia",
    label: "Modules et IA",
    description: "Modules affichés, vue avancée de l'IA et modèles utilisés.",
    icon: Sparkles,
    group: "cabinet",
    audience: "admin",
  },
  {
    slug: "consommation",
    label: "Consommation",
    description: "Usage du cabinet au regard des limites de sa formule.",
    icon: BarChart3,
    group: "cabinet",
    audience: "admin",
  },
  {
    slug: "journal",
    label: "Journal d'audit",
    description: "Qui a fait quoi, et quand, dans le cabinet.",
    icon: History,
    group: "cabinet",
    audience: "admin",
  },
];

/** Libellé de groupe, selon le rôle (« · admin » ne parle qu'aux admins). */
export function settingsGroupLabel(group: SettingsGroup, isAdmin: boolean): string {
  if (group === "me") return "Moi";
  return isAdmin ? "Cabinet · admin" : "Cabinet";
}

function visibleTo(section: SettingsSection, isAdmin: boolean): boolean {
  if (section.audience === "all") return true;
  return section.audience === "admin" ? isAdmin : !isAdmin;
}

/** Sections visibles pour ce rôle, dans l'ordre du menu. */
export function settingsSectionsFor(isAdmin: boolean): SettingsSection[] {
  return SETTINGS_SECTIONS.filter((section) => visibleTo(section, isAdmin));
}

/** Sections regroupées (« Moi », puis « Cabinet »), groupes vides omis. */
export function settingsGroupsFor(
  isAdmin: boolean,
): { group: SettingsGroup; label: string; sections: SettingsSection[] }[] {
  const sections = settingsSectionsFor(isAdmin);
  return (["me", "cabinet"] as const)
    .map((group) => ({
      group,
      label: settingsGroupLabel(group, isAdmin),
      sections: sections.filter((section) => section.group === group),
    }))
    .filter((entry) => entry.sections.length > 0);
}

export function findSettingsSection(slug: string | undefined): SettingsSection | undefined {
  return SETTINGS_SECTIONS.find((section) => section.slug === slug);
}

/** Vrai si ce rôle peut ouvrir la sous-page `slug`. */
export function canOpenSettingsSection(slug: string, isAdmin: boolean): boolean {
  const section = findSettingsSection(slug);
  return section !== undefined && visibleTo(section, isAdmin);
}

export function settingsPath(slug: SettingsSlug): string {
  return `/reglages/${slug}`;
}

/** Anciens onglets `/reglages?tab=…` (favoris, e-mails) → sous-page. */
const LEGACY_TABS: Record<string, SettingsSlug> = {
  organization: "cabinet",
  team: "equipe",
  templates: "modeles",
  llm: "modules-ia",
  features: "automatisations",
  security: "securite",
  audit: "journal",
  profile: "profil",
};

/** Étape d'onboarding admin → sous-page où la franchir. */
const ONBOARDING_TARGETS: Partial<Record<OnboardingStatusResponse["next_step"], SettingsSlug>> = {
  CABINET_PROFILE: "identite-reglementaire",
  TEAM_SETUP: "equipe",
};

/**
 * Sous-page ouverte par `/reglages` : l'ancien onglet demandé s'il est
 * permis, sinon l'étape d'onboarding en cours (admin), sinon `null` — la
 * page affiche alors la liste (téléphone) ou la première section (desktop).
 */
export function requestedSettingsSlug({
  isAdmin,
  legacyTab,
  onboardingStep,
}: {
  isAdmin: boolean;
  legacyTab?: string | null;
  onboardingStep?: OnboardingStatusResponse["next_step"] | null;
}): SettingsSlug | null {
  const fromTab = legacyTab ? LEGACY_TABS[legacyTab] : undefined;
  if (fromTab && canOpenSettingsSection(fromTab, isAdmin)) return fromTab;
  const fromOnboarding = onboardingStep ? ONBOARDING_TARGETS[onboardingStep] : undefined;
  if (isAdmin && fromOnboarding) return fromOnboarding;
  return null;
}

/** Première sous-page du menu pour ce rôle. */
export function firstSettingsSlug(isAdmin: boolean): SettingsSlug {
  return settingsSectionsFor(isAdmin)[0]!.slug;
}
